import { ChatVersion } from 'app/copied/constants/common';

import { logInfo } from './logger';

export enum StorageStrategy {
  LOCAL_STORAGE = 'localStorage',
  SESSION_STORAGE = 'sessionStorage',
}

export enum ModuleType {
  Webchat = 'webchat',
  CTA = 'cta',
  Adstra = 'adstra',
  Throtle = 'throtle',
  Posthog = 'posthog',
  ExternalGA4 = 'external-ga4',
  Signal = 'signal',
  Storage = 'storage',
}

export interface IBaseModuleConfig {
  module: string;
  module_type: ModuleType;
}

export interface IWebChatModuleConfig extends IBaseModuleConfig {
  condition_uuid: string;
  enabled: boolean;
  exclude_on_urls: string[];
  language_codes: string[];
  localization_title: string;
  web_chat_version: ChatVersion;
}

export type IModuleConfig = IWebChatModuleConfig; // | IAdstraModule | IThrotleModule | ICtaModule; // introduce other of the modules if needed

interface IModule {
  module: string;
  module_type: ModuleType;
}

interface IModuleManager {
  modules: IModule[];
  webchatModuleConfigs: IWebChatModuleConfig[];
}

export interface IStorageManager {
  strategy: StorageStrategy;
  getItem: typeof Storage.prototype.getItem;
  setItem: typeof Storage.prototype.setItem;
  removeItem: typeof Storage.prototype.removeItem;
  clear: typeof Storage.prototype.clear;
}

export type EventCaptureHandler = (eventName: string, eventParams?: object) => void;

interface IAnalyticsManager {
  captureEvent: EventCaptureHandler;
  addPersistentParams: (params: object) => void;
}

export interface ISignalModule extends IModule, EventTarget {}

export interface OstroManager {
  moduleManager: IModuleManager;
  storage: IStorageManager;
  analytics: IAnalyticsManager;

  conditionId: string;
  featureFlags: object;
  ostroId: string;
  rxId: string;
}

/**
 * This class is meant to be used only when running in localhost, as ostro.js is not run and thus no ID handler is present.
 * When running in higher environments, `window.ostro` will be properly defined because ostro.js will run first.
 */
class LocalBoostrapOstroManager implements OstroManager {
  private _conditionId = '';
  private _rxId = '';
  private _featureFlags: object = {};
  ostroId = '';
  exclude_on_urls: string[] = [];

  moduleManager: IModuleManager = { modules: [], webchatModuleConfigs: [] };

  storage: IStorageManager = {
    strategy: StorageStrategy.LOCAL_STORAGE,
    getItem: (key: string) => localStorage.getItem(key),
    setItem: (key: string, value: string) => localStorage.setItem(key, value),
    removeItem: (key: string) => localStorage.removeItem(key),
    clear: () => localStorage.clear(),
  };

  analytics: IAnalyticsManager = {
    addPersistentParams: (params: object) => {
      logInfo('addPersistentParams', params, JSON.stringify(params));
    },
    captureEvent: (eventName: string, eventParams?: object) => {
      logInfo('captureEvent', eventName, eventParams, JSON.stringify(eventParams));
    },
  };

  get conditionId(): string {
    return this._conditionId || this.storage.getItem('rx_condition_id') || '';
  }

  set conditionId(newConditionId: string) {
    newConditionId ??= '';
    if (newConditionId !== this._conditionId) {
      // Move the rx ID to the new key
      this.storage.removeItem(this._conditionId);

      if (this._rxId) {
        this.storage.setItem(newConditionId, this._rxId);
      }

      this._conditionId = newConditionId;
      this.storage.setItem('rx_condition_id', newConditionId);
    }
  }

  get rxId(): string {
    return this._rxId;
  }

  set rxId(newRxId: string) {
    newRxId ??= '';
    if (newRxId !== this._rxId) {
      this._rxId = newRxId;
      this.storage.setItem(this._conditionId, newRxId);
    }
  }

  get featureFlags(): object {
    return this._featureFlags;
  }

  set featureFlags(newFeatureFlags: object) {
    newFeatureFlags ??= {};
    const stringifiedNewFeatureFlags = JSON.stringify(newFeatureFlags);
    if (JSON.stringify(this._featureFlags) !== stringifiedNewFeatureFlags) {
      this._featureFlags = newFeatureFlags;
      this.storage.setItem('rx_feature_flags', stringifiedNewFeatureFlags);
    }
  }
}

class OstroAdapter {
  ostro: OstroManager;

  constructor() {
    this.ostro = window.ostro || new LocalBoostrapOstroManager();
  }

  getStorageMode(): StorageStrategy {
    return this.ostro.storage.strategy;
  }

  getRxId(): string {
    return this.ostro.rxId || '';
  }

  getConditionId(): string {
    return this.ostro.conditionId || '';
  }

  getWebchatModules(): IWebChatModuleConfig[] {
    return this.ostro.moduleManager.webchatModuleConfigs;
  }

  getFeatureFlags(): object {
    return this.ostro.featureFlags || {};
  }
}

export const ostroAdapter = new OstroAdapter();
